import { isDefined } from "@clipboard-health/util-ts";
import {
  type ClipboardScoreProfileResponse,
  type EmergencyCreditStatsCommon,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";

interface UseEmergencyCreditStatsParams {
  clipboardScoreProfile?: ClipboardScoreProfileResponse;
}

interface UseEmergencyCreditStatsResult {
  cancellationCreditStats: EmergencyCreditStatsCommon;
  latenessCreditStats: EmergencyCreditStatsCommon;

  /**
   * The number of shifts completed towards the next lateness credit if any for displaying via progress bar
   */
  completedShiftsToDisplayForLatenessCredit: number;

  /**
   * Whether the worker has lateness credit available
   */
  hasAvailableLatenessCredit: boolean;
}

/**
 * TODO: move this into /utils in separate PR
 * Extracts emergency credit stats from the clipboard score profile.
 * Returns undefined when the profile has no emergency credit data
 * (i.e. the EC feature flag is off for this worker).
 */
export function useEmergencyCreditStats(
  params: UseEmergencyCreditStatsParams
): UseEmergencyCreditStatsResult | undefined {
  const { clipboardScoreProfile } = params;
  const { emergencyCreditStats } = clipboardScoreProfile ?? {};

  if (!isDefined(emergencyCreditStats)) {
    return undefined;
  }

  const cancellationCreditStats = emergencyCreditStats.cancellation;
  const latenessCreditStats = emergencyCreditStats.lateness;

  const hasAvailableLatenessCredit = latenessCreditStats.totalAvailableCredits > 0;

  // If credit is available but no progress yet, show bar as complete
  const completedShiftsToDisplayForLatenessCredit = hasAvailableLatenessCredit
    ? latenessCreditStats.requiredShiftsForNextCredit
    : latenessCreditStats.completedShiftsTowardsNextCredit;

  return {
    cancellationCreditStats,
    latenessCreditStats,
    completedShiftsToDisplayForLatenessCredit,
    hasAvailableLatenessCredit,
  };
}
